import React from "react";

interface ArcProgressProps {
    size?: number;
    strokeWidth?: number;
    progress: number;
    startAngle?: number;
    endAngle?: number;
    color?: string;
    bgColor?: string;
    text?: string;
    textColor?: string;
    fontSize?: number;
}

const polarToCartesian = (
    cx: number,
    cy: number,
    r: number,
    angle: number
) => {
    const a = (angle - 90) * Math.PI / 180;
    return {
        x: cx + r * Math.cos(a),
        y: cy + r * Math.sin(a),
    };
};

const describeArc = (
    cx: number,
    cy: number,
    r: number,
    startAngle: number,
    endAngle: number
) => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `
    M ${start.x} ${start.y}
    A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}
  `;
};

export const ArchProgress: React.FC<ArcProgressProps> = ({
                                                            size = 200,
                                                            strokeWidth = 12,
                                                            progress,
                                                            startAngle = -180,
                                                            endAngle = 0,
                                                            color = "#4ade80",
                                                            bgColor = "#e5e7eb",
                                                            text = "",
                                                            textColor = "#111",
                                                            fontSize = 14,
                                                        }) => {
    const center = size / 2;
    const radius = center - strokeWidth / 2;

    const clamped = Math.min(Math.max(progress, 0), 100);

    const fullArc = describeArc(center, center, radius, startAngle, endAngle);

    const progressAngle =
        startAngle + (endAngle - startAngle) * clamped / 100;

    const progressArc = describeArc(
        center,
        center,
        radius,
        startAngle,
        progressAngle
    );

    // snap logic
    const snapToCenter = clamped <= 2 || clamped > 90;

    const textPos = snapToCenter
        ? { x: center, y: size - strokeWidth / 2}
        : polarToCartesian(
            center,
            center,
            radius,
            progressAngle + 17
        );

    const textRotation = snapToCenter
        ? 0
        : progressAngle + 17 + 180;

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <path
                d={fullArc}
                stroke={bgColor}
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
            />

            {clamped > 0 && (
                <path
                    d={progressArc}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeLinecap="round"
                />
            )}

            {text &&
            <text
                x={textPos.x}
                y={textPos.y}
                fill={textColor}
                fontSize={fontSize}
                fontWeight="600"
                textAnchor="middle"
                dominantBaseline="middle"
                transform={
                    snapToCenter
                        ? undefined
                        : `rotate(${textRotation} ${textPos.x} ${textPos.y})`
                }
            >
                {text}
            </text>}
        </svg>
    );
};
